import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ShieldCheck } from "lucide-react";
import { COMPANY_INFO } from "../data";
import { fetchCompanyInfo } from "../lib/cms";

export default function Footer() {
  const [companyInfo, setCompanyInfo] = useState(COMPANY_INFO);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchCompanyInfo().then((info) => {
      if (info) setCompanyInfo(info);
    });
  }, []);

  const links = [
    { label: "Forside", path: "/" },
    { label: "Totalrenovering", path: "/totalrenovering" },
    { label: "Ombygning", path: "/ombygning" },
    { label: "Totalentreprise", path: "/totalentreprise" },
    { label: "Tilbygninger", path: "/ydelser/tilbygninger" },
    { label: "Træterrasser", path: "/ydelser/traeterrasser" },
    { label: "Døre og vinduer", path: "/ydelser/doere-og-vinduer" },
    { label: "Tagarbejde", path: "/ydelser/tagarbejde" },
    { label: "Tidligere projekter", path: "/tidligere-projekter" },
    { label: "Galleri", path: "/galleri" },
    { label: "Kontakt", path: "/kontakt" },
  ];

  return (
    <footer className="bg-primary-900 border-t border-primary-800 text-primary-200">
      {/* Main Footer Content */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-12">
          {/* Company Info Column */}
          <div className="space-y-6">
            <div className="inline-flex rounded-2xl bg-primary-50 px-4 py-3 shadow-sm">
              <img
                src={companyInfo.logo}
                alt={companyInfo.name || "Sehested Tømrerfirma ApS"}
                className="h-[85px] w-auto object-contain"
              />
            </div>

            <p className="text-sm text-primary-400 max-w-xs">
              {companyInfo.description || "Din lokale tømrer i Aarhus Syd. Vi leverer solidt håndværk, personlig kontakt og høj kvalitet til dit næste byggeprojekt."}
            </p>

            {/* Membership Badges */}
            <div className="flex items-center gap-4 pt-2">
              <div className="h-12 w-32 flex items-center justify-center">
                <img
                  src={companyInfo.bygGarantiLogo}
                  alt="Byg Garanti"
                  className="h-full w-auto object-contain"
                />
              </div>
              <div className="h-12 w-32 flex items-center justify-center">
                <img
                  src={companyInfo.diByggeriLogo}
                  alt="DI Byggeri"
                  className="h-full w-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white mb-6">
              Hurtige links
            </h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              {links.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-primary-400 hover:text-brand-400 transition-colors duration-150"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details Column */}
          <div className="space-y-6">
            <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white">
              Kontakt os
            </h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-brand-500 shrink-0 mt-0.5" />
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(companyInfo.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-400 hover:text-brand-400 transition-colors duration-150"
                >
                  {companyInfo.address}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-brand-500 shrink-0" />
                <a
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="text-primary-400 hover:text-brand-400 transition-colors duration-150"
                >
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-brand-500 shrink-0" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-primary-400 hover:text-brand-400 transition-colors duration-150"
                >
                  {companyInfo.email}
                </a>
              </li>
              <li className="pt-2 text-xs text-primary-500">
                CVR-nummer: {companyInfo.cvr}
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 border-t border-primary-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-primary-500">
          <p>© {currentYear} Sehested Tømrerfirma ApS. Alle rettigheder forbeholdes.</p>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand-600" />
            <span>Medlem af Byg Garanti & DI Byggeri</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
